<template>
  <template v-if="items.length">
    <button
      type="button"
      class="btn btn-sm btn-icon btn-outline-secondary"
      aria-haspopup="true"
      :aria-controls="menuId"
      :title="t('common.actions.title')"
      :disabled="busy"
      @click="menu?.toggle($event)"
    >
      <FontAwesomeIcon
        :icon="spinning ? 'spinner' : ['fas', 'ellipsis-v']"
        :class="{ 'fa-spin': spinning }"
      />
    </button>
    <Menu
      :id="menuId"
      ref="menu"
      :model="items"
      popup
      :pt="{
        root: { class: 'dropdown-menu show' },
        list: { class: 'list-unstyled mb-0' },
      }"
    >
      <template #item="{ item, props: itemProps }">
        <a
          v-bind="itemProps.action"
          class="dropdown-item"
          :class="[item.class, { disabled: busy }]"
        >
          <FontAwesomeIcon :icon="item.faIcon" class="me-2" />
          {{ item.label }}
        </a>
      </template>
    </Menu>
  </template>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";
import Menu from "primevue/menu";
import { computed, useId, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import type { CompanyOrder } from "../common/companyOrderTypes";

/**
 * The actions a user can take on an order, listing only those the server accepts on it now
 * (`allowed_actions`); renders nothing when there are none. The menu is a popup attached to the page
 * body, so a virtually scrolled table cannot clip it.
 */
const props = defineProps<{
  order: CompanyOrder;
  busy: boolean;
  spinning: boolean;
}>();

const emit = defineEmits<{
  confirm: [];
  reject: [];
}>();

const { t } = useI18n();
const menu = useTemplateRef<InstanceType<typeof Menu>>("menu");
const menuId = useId();

const items = computed<MenuItem[]>(() => {
  const allowed = props.order.allowed_actions;
  return [
    ...(allowed.includes("CONFIRM")
      ? [
          {
            label: t("company_orders.confirm"),
            faIcon: ["far", "check"],
            class: "text-success",
            disabled: () => props.busy,
            command: () => emit("confirm"),
          },
        ]
      : []),
    ...(allowed.includes("CANCEL")
      ? [
          {
            label: t("company_orders.reject"),
            faIcon: ["far", "xmark"],
            class: "text-danger",
            disabled: () => props.busy,
            command: () => emit("reject"),
          },
        ]
      : []),
  ];
});
</script>
